import type { Theme } from '@/types/global.types'

// Cache theme settings to avoid repeated DOM queries and JSON parsing
let themeSettingsCache: Theme = {} as Theme

/**
 * Retrieves the theme settings from the HTML element with the id "themeSettings",
 * and returns them as a JavaScript object.
 * Results are cached after the first call for performance.
 *
 * @returns {object} The theme settings as a JavaScript object.
 */
export function getThemeSettings(): Theme {
  const themeSettings = document.querySelector('#themeSettings')?.textContent
  themeSettingsCache = JSON.parse(themeSettings || '{}')
  return themeSettingsCache
}

export function getAppVersion(): string | null | undefined {
  return document.querySelector('#appVersion')?.textContent
}

/**
 * Retrieves the localized value for a given key from the theme settings.
 * If the locale-specific value is not found, returns the key itself.
 *
 * @param {string} key - The key to look up in the localized settings.
 * @returns {string} The localized value for the given key, or the key itself if not found.
 */
export function i18n(key: string) {
  const themeSettings = getThemeSettings()
  return themeSettings?.i18n?.[key] || key
}

/**
 * Retrieves the dashboard modules from the theme settings.
 * Uses cached theme settings for performance.
 * @returns {Array} An array of dashboard modules.
 */
export function getDashboardModules() {
  const themeSettings = getThemeSettings()
  return themeSettings?.dashboard || []
}

/**
 * Retrieves the formats (silos) from the theme settings.
 * Uses cached theme settings for performance.
 * @returns {Array} An array of formats.
 */
export function getFormats() {
  const themeSettings = getThemeSettings()
  return themeSettings?.silos || []
}
